import {t, Trans} from '@lingui/macro';
import {flatten} from 'lodash';
import React, {FC, ReactNode, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {TextBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Dropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemCheckbox} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemCheckbox';
import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownList} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {DropdownItemIcon} from '../../../../components/common/dropdowns/dropdownItemIcon';
import {fuzzySearchBuilder} from '../../../../helpers/fuzzySearchHelpers';
import {Localizer} from '../../../../store/stateless/internationalizationSelectors';
import type {MultipleGroupsDropdownBoxProps} from './multipleGroupsDropdownBox';
import {
  GroupTitle,
  MultipleGroupsDropdownGroup,
  StyledDropdownGroupTitleContent,
} from './multipleGroupsDropdownGroup';
import type {DropdownGroup} from './multipleGroupsDropdownHelpers';

/*
 * Props.
 */

type MultipleGroupsDropdownProps<T> = MultipleGroupsDropdownBoxProps<T> & {
  popoverProps: PopoverTransferProps;
};

/*
 * Style.
 */

const DropdownSearch = styled.div`
  background-color: ${(p) => p.theme.greys.white};

  padding: 11px;
  border-bottom: 1px solid ${(p) => p.theme.alphas.black20};
`;

const StyledDropdownList = styled(DropdownList)<{$hasSearch: boolean}>`
  ${(p) => (p.$hasSearch ? 'padding-top: 0;' : '')};
`;

const AnyDropdownItem = styled(DropdownItem)`
  border-bottom: 1px solid ${(p) => p.theme.greys.shade20};
`;

const StyledIcon = styled(Icon)`
  grid-area: right-icon;
  align-self: center;
`;

/*
 * Component.
 */

export const MultipleGroupsDropdown = <T,>(props: MultipleGroupsDropdownProps<T>): ReturnType<FC> => {
  const localize = useLocalize();
  const {
    popoverProps,
    selectedItems,
    isSearchDisabled,
    canSelectMultipleItems,
    canSelectAnyItem,
    anyLabel,
    anyIcon,
    onChange,
    onSelectCreateExpression,
    onSearch,
  } = props;

  const [searchQuery, setSearchQuery] = useState('');
  const anyItemSelected = canSelectAnyItem && selectedItems?.length === 0;
  const shouldSelectFirst = Boolean(searchQuery) && props.shouldMergeGroupsWhenSearch;
  const filterValue = shouldSelectFirst ? searchQuery : undefined;

  function handleChange(item: T) {
    if (!canSelectMultipleItems) {
      onChange([item]);
      popoverProps.onRequestClose();
    } else if (selectedItems?.includes(item)) {
      onChange(selectedItems.filter((i) => i !== item));
    } else {
      onChange([...(selectedItems || []), item]);
    }
  }

  const groups = useMemo(() => {
    // If we have `onSearch` prop, filtering is done outside of the component
    if (searchQuery === '' || onSearch) {
      return props.groups;
    }

    if (props.shouldMergeGroupsWhenSearch) {
      const genericGroup = props.groups[0];
      const mergedItems = flatten(props.groups.map((group) => group.items));
      const fuzzySearch = fuzzySearchBuilder(genericGroup.serializeItemForSearch);
      return [
        {
          ...genericGroup,
          title: undefined,
          items: fuzzySearch(mergedItems, searchQuery),
        },
      ];
    }

    return props.groups.map((group) => {
      const fuzzySearch = fuzzySearchBuilder(group.serializeItemForSearch);
      return {...group, items: fuzzySearch(group.items, searchQuery)};
    });
  }, [searchQuery, props.groups, props.shouldMergeGroupsWhenSearch]);

  useEffect(() => {
    if (onSearch) {
      onSearch(searchQuery);
    }
  }, [searchQuery]);

  return (
    <Dropdown {...popoverProps} {...props} width={props.width ?? popoverProps.anchorWidth} maxHeight={400}>
      {maybeRenderSearchInput(localize, searchQuery, setSearchQuery, isSearchDisabled)}
      <StyledDropdownList
        $hasSearch={!isSearchDisabled}
        {...props}
        shouldSelectFirst={shouldSelectFirst}
        filterValue={filterValue}
      >
        {maybeRenderAnyInputItem<T>(onChange, anyLabel, anyIcon, canSelectAnyItem, anyItemSelected)}
        {maybeRenderCreateExpression<T>(
          Boolean(searchQuery),
          groups,
          onSelectCreateExpression,
          popoverProps.onRequestClose,
        )}
        {groups.map((group) =>
          maybeRenderDropdownGroup<T>(group, selectedItems, canSelectMultipleItems, handleChange),
        )}
      </StyledDropdownList>
    </Dropdown>
  );
};

/*
 * Helpers.
 */

function maybeRenderSearchInput(
  localize: Localizer,
  searchQuery: string,
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>,
  isSearchDisabled?: boolean,
) {
  if (isSearchDisabled) {
    return null;
  }

  return (
    <DropdownSearch key="search">
      <TextBox
        noMargin
        placeholder={localize(t('search')`Search`)}
        value={searchQuery}
        onChange={setSearchQuery}
        shouldFocus
      />
    </DropdownSearch>
  );
}

function maybeRenderAnyInputItem<T>(
  onChange: HandlerOf<ReadonlyArray<T>>,
  anyLabel?: ReactNode,
  anyIcon?: ReactNode,
  canSelectAnyItem?: boolean,
  anyItemSelected?: boolean,
) {
  if (!canSelectAnyItem) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <AnyDropdownItem
          renderRightContent={() => <DropdownItemCheckbox isChecked={anyItemSelected} />}
          isChecked={anyItemSelected}
          onSelect={() => onChange([])}
        >
          {anyIcon ? (
            <DropdownItemIcon color={theme.palette.teal.shade40} label={anyLabel}>
              {anyIcon}
            </DropdownItemIcon>
          ) : null}
          <DropdownItemContent isChecked={anyItemSelected}>{anyLabel}</DropdownItemContent>
        </AnyDropdownItem>
      )}
    </UserThemeConsumer>
  );
}

function maybeRenderCreateExpression<T>(
  hasActiveSearch: boolean,
  groups: readonly DropdownGroup<any>[],
  onSelectCreateExpression: Handler | undefined,
  onPopoverClose: Handler,
) {
  if (!onSelectCreateExpression) {
    return null;
  }

  const hasAnyItem = groups.some((group) => group.items.length > 0);
  if (hasActiveSearch && hasAnyItem) {
    return null;
  }

  return (
    <>
      {hasAnyItem ? (
        <GroupTitle key="group-create-new-expression" isNotInteractive>
          <StyledDropdownGroupTitleContent>
            <Trans id="create-expression-title">Create</Trans>
          </StyledDropdownGroupTitleContent>
        </GroupTitle>
      ) : null}
      <UserThemeConsumer>
        {(theme) => (
          <DropdownItem
            renderRightContent={() => (
              <StyledIcon name="chevronRightSmall" color={theme.palette.grey.shade30} />
            )}
            onSelect={() => {
              onSelectCreateExpression();
              onPopoverClose();
            }}
          >
            <DropdownItemIcon
              iconSize={16}
              iconName="plusCircleFilledSmall"
              color={theme.palette.blue.shade40}
              label="Create"
            />
            <DropdownItemContent>
              <Trans id="create-expression-content">New dynamic variable</Trans>
            </DropdownItemContent>
          </DropdownItem>
        )}
      </UserThemeConsumer>
    </>
  );
}

function maybeRenderDropdownGroup<T>(
  group: DropdownGroup<T>,
  selectedItems: MultipleGroupsDropdownProps<T>['selectedItems'],
  canSelectMultipleItems: MultipleGroupsDropdownProps<T>['canSelectMultipleItems'],
  handleChange: HandlerOf<T>,
) {
  if (!group.items.length) {
    return null;
  }

  return (
    <MultipleGroupsDropdownGroup
      key={`group-${group.getItemKey(group.items[0])}`}
      group={group}
      canSelectMultipleItems={canSelectMultipleItems}
      selectedItems={selectedItems}
      onChange={handleChange}
    />
  );
}
